import { useEffect } from 'react';
import { toHiragana, toRomaji } from 'wanakana';

export default function WordPopup({ token, entry, position, onClose, onSave, isSaved }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose);
    };
  }, [onClose]);

  const rawReading = token.reading || entry?.japanese?.[0]?.reading || '';
  const reading = rawReading ? toHiragana(rawReading) : '';
  const romaji = rawReading ? toRomaji(rawReading) : '';
  const showReading = reading && reading !== token.surface_form;

  const popupStyle = calcPosition(position);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
      <div style={{
        position: 'fixed',
        zIndex: 1000,
        background: 'white',
        border: '1px solid #e8e6e1',
        borderRadius: 14,
        padding: '18px 22px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
        maxWidth: 320,
        minWidth: 200,
        ...popupStyle,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif' }}>
              {token.surface_form}
            </div>
            {showReading && (
              <div style={{ fontSize: 13, color: '#666', marginTop: 5 }}>
                {reading} <span style={{ color: '#aaa', margin: '0 4px' }}>·</span> {romaji}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 16, padding: '10px', lineHeight: 1, flexShrink: 0, margin: '-10px -10px -10px 0' }}
          >✕</button>
        </div>

        <div style={{ height: 1, background: '#f0ede8', margin: '14px 0' }} />

        {entry && entry !== 'offline' && onSave && (
          <button
            onClick={() => onSave(token, entry)}
            disabled={isSaved}
            style={{
              width: '100%', padding: '7px 0', marginBottom: 14,
              fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
              cursor: isSaved ? 'default' : 'pointer',
              background: isSaved ? '#e8f5e9' : '#4a90d9',
              color: isSaved ? '#4caf50' : 'white',
              transition: 'background 0.2s',
            }}
          >
            {isSaved ? '✓ Saved' : '+ Save to word list'}
          </button>
        )}

        {entry && entry !== 'offline' ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {entry.senses?.[0]?.parts_of_speech?.[0] && (
                <span style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {entry.senses[0].parts_of_speech[0]}
                </span>
              )}
              {entry.jlpt?.[0] && (
                <span style={jlptBadgeStyle(entry.jlpt[0])}>
                  {entry.jlpt[0].replace('jlpt-', '').toUpperCase()}
                </span>
              )}
              {entry.is_common && (
                <span style={{ fontSize: 10, color: '#4caf50', fontWeight: 600 }}>common</span>
              )}
            </div>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              {entry.senses?.slice(0, 5).map((sense, i) => (
                <li key={i} style={{ fontSize: 14, color: '#333', marginBottom: 5, lineHeight: 1.5 }}>
                  {sense.english_definitions?.join(', ')}
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p style={{ color: '#aaa', margin: 0, fontSize: 14, fontStyle: 'italic' }}>
            {entry === 'offline' ? 'Offline: no definition found' : 'No definition found'}
          </p>
        )}
      </div>
    </>
  );
}

function jlptBadgeStyle(jlpt) {
  const colors = { 'jlpt-n5': '#4caf50', 'jlpt-n4': '#8bc34a', 'jlpt-n3': '#ff9800', 'jlpt-n2': '#ff5722', 'jlpt-n1': '#e53935' };
  return {
    fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
    background: colors[jlpt] || '#999', color: 'white', letterSpacing: 0.5,
  };
}

function calcPosition({ x, y, viewportWidth, viewportHeight }) {
  const W = 320, H = 280, GAP = 10;
  let left = x;
  let top = y + GAP;
  if (left + W > viewportWidth - 16) left = viewportWidth - W - 16;
  if (left < 16) left = 16;
  if (top + H > viewportHeight - 16) top = y - H - GAP;
  return { left, top };
}
